// Pure derivation helpers for the Budget Enforcement Overview band (verdict +
// KPI tiles) and the scope table. No React/router/api imports so this is unit
// testable in isolation.

// A budget counts as an "imminent" breach when its predicted breach is within
// this many days. Drives the Imminent breaches tile + amber verdict.
export const IMMINENT_DAYS = 14;

// normalizeStatus maps backend budget_eval_daily.status values to canonical
// internal values used for badges, counts, and sorting.
//   "ok"                            → "ok"
//   "warn"                          → "at_risk"
//   "breached_actual" | "breached_forecast" → "breaching"
//   anything else                   → "unknown"
export function normalizeStatus(backendStatus) {
  switch (backendStatus) {
    case "ok":
      return "ok";
    case "warn":
      return "at_risk";
    case "breached_actual":
    case "breached_forecast":
      return "breaching";
    default:
      return "unknown";
  }
}

// A row "needs attention" if it is breaching or at risk.
export function isAttention(row) {
  const n = normalizeStatus(row && row.status);
  return n === "breaching" || n === "at_risk";
}

// statusRank orders rows worst-first: actual breach < forecast breach < warn < ok.
export function statusRank(row) {
  switch (row && row.status) {
    case "breached_actual":
      return 0;
    case "breached_forecast":
      return 1;
    case "warn":
      return 2;
    case "ok":
      return 3;
    default:
      return 4;
  }
}

// sortScopeRows returns a new array sorted worst-first, tie-broken by higher
// utilization. Does not mutate the input.
export function sortScopeRows(rows) {
  return [...(rows || [])].sort(
    (a, b) => statusRank(a) - statusRank(b) || (b.utilization_pct || 0) - (a.utilization_pct || 0)
  );
}

// utilizationPct = actual / budget as a whole-number percent (0 when no budget).
// Not capped — a breaching budget reads above 100%.
export function utilizationPct(row) {
  const budget = row && row.budget;
  const actual = (row && row.actual) || 0;
  return budget > 0 ? Math.round((actual / budget) * 100) : 0;
}

// scopeLevelLabel renders a budget's scope level (a dimension code) as a perspective
// label. The four canonical perspectives match the /cost-allocation/hierarchy page exactly
// — Organisation / Financial / Technical / Use-case — normalising the env-specific code
// spellings the same way the hierarchy does (see HierarchyManager DIMENSION_ICONS). Unknown
// codes title-case as a graceful fallback (e.g. "team" → "Team"). This is the FALLBACK label;
// when the live budget-dimensions are available the page passes their display names through.
export function scopeLevelLabel(level) {
  const norm = String(level ?? "").toLowerCase().replace(/[\s_-]/g, "");
  switch (norm) {
    case "org":
    case "organisation":
    case "organization":
      return "Organisation";
    case "financial":
    case "finance":
    case "cost":
    case "costs":
      return "Financial";
    case "technical":
    case "technology":
    case "tech":
      return "Technical";
    case "usecase":
    case "usecases":
    case "aiusecase":
    case "uc":
      return "Use-case";
    default:
      return level ? level.charAt(0).toUpperCase() + level.slice(1) : "—";
  }
}

// deriveSummary computes the verdict + tile counts from the /overview payload.
// Returns counts, an overall status (red|amber|green), and the severity-ranked
// primaryDrill perspective (breaching > imminent > approvals) for the verdict's
// "start here" link.
export function deriveSummary(overview) {
  const scope = (overview && overview.scope_status) || [];
  const timeline = (overview && overview.breach_timeline) || [];
  const approvals = (overview && overview.pending_approvals) || [];

  const breachingRows = scope.filter((r) => normalizeStatus(r.status) === "breaching");
  const breachingCount = breachingRows.length;
  const overageEur = breachingRows.reduce((s, r) => s + (r.projected_overage || 0), 0);

  // Only count a budget as an imminent breach if it ALSO reads as attention in the table — a
  // budget that Financial Control calls "on track" (its projected overage is immaterial) has a
  // breach ETA but shouldn't make the verdict say "1 imminent breach" while the table is all-clear.
  const attentionIds = new Set(scope.filter(isAttention).map((r) => r.budget_id));
  const imminentRows = timeline.filter(
    (r) => r.breach_eta_days != null && r.breach_eta_days <= IMMINENT_DAYS && attentionIds.has(r.budget_id)
  );
  const imminentCount = imminentRows.length;
  const soonest = imminentRows.reduce(
    (min, r) => (min == null || r.breach_eta_days < min.breach_eta_days ? r : min),
    null
  );

  const approvalsCount = approvals.length;
  const anyAtRisk = scope.some((r) => normalizeStatus(r.status) === "at_risk");

  // Governance posture — the page's headline lens (governed vs ungoverned), all derivable
  // from scope_status (each row carries policy_id + actual; policy_id is 0 when ungoverned).
  // NOTE: the denominator is the open-active eval set this overview already scopes to, not the
  // entire budget portfolio.
  const governedRows = scope.filter((r) => !!r.policy_id);
  const ungovernedRows = scope.filter((r) => !r.policy_id);
  const totalSpend = scope.reduce((s, r) => s + (r.actual || 0), 0);
  const governedSpend = governedRows.reduce((s, r) => s + (r.actual || 0), 0);
  const governance = {
    totalCount: scope.length,
    governedCount: governedRows.length,
    ungovernedCount: ungovernedRows.length,
    ungovernedSpendEur: ungovernedRows.reduce((s, r) => s + (r.actual || 0), 0),
    coveragePct: totalSpend > 0 ? governedSpend / totalSpend : 0,
  };

  let status = "green";
  if (breachingCount > 0) status = "red";
  else if (imminentCount > 0 || approvalsCount > 0 || anyAtRisk) status = "amber";

  let primaryDrill = null;
  if (breachingCount > 0) primaryDrill = "breaching";
  else if (imminentCount > 0) primaryDrill = "imminent";
  else if (approvalsCount > 0) primaryDrill = "approvals";

  // primaryBreach is the single governed breaching row — the case the hero can
  // speak to in root-cause + fix terms ("X crossed its hard cap"). Only when
  // EXACTLY ONE row is breaching AND it has a governing policy; otherwise null
  // and the verdict falls back to the multi-stream headline.
  const policies = (overview && overview.policies) || [];
  let primaryBreach = null;
  if (breachingCount === 1 && effectivePolicy(breachingRows[0], policies)) {
    primaryBreach = riskRowModel(breachingRows[0], policies);
  }

  return {
    status,
    primaryDrill,
    confidence: (overview && overview.forecast_confidence) || null,
    primaryBreach,
    breaching: { count: breachingCount, overageEur },
    imminent: { count: imminentCount, soonest },
    approvals: { count: approvalsCount },
    governance,
  };
}

// effectivePolicy returns the policy governing a scope row (matched by
// row.policy_id), or null when the row is ungoverned / the policy is missing.
export function effectivePolicy(row, policies) {
  if (!row || !row.policy_id) return null;
  return (policies || []).find((p) => p.id === row.policy_id) || null;
}

const clampPct = (v) => (v == null ? null : Math.max(0, Math.min(100, v)));

// thresholdMarks returns the soft/hard threshold positions (0..100) for a
// policy's utilization track. A null policy yields both marks null.
export function thresholdMarks(policy) {
  if (!policy) return { soft: null, hard: null };
  return { soft: clampPct(policy.soft_pct), hard: clampPct(policy.hard_pct) };
}

// policyHasSignoffGate reports whether a policy actually escalates to sign-off — i.e. it has at
// least one step whose action is "approval_gate". The page must only promise sign-off when this
// is true (the FE previously fabricated a "hard → sign-off" rung that didn't exist).
export function policyHasSignoffGate(policy) {
  return !!policy && Array.isArray(policy.steps) && policy.steps.some((s) => s && s.action === "approval_gate");
}

// humanizeBreachEta renders a breach ETA in days as a short relative phrase.
//   null → null; <=0 → "today"; 1 → "tomorrow"; n → "in n days".
export function humanizeBreachEta(days) {
  if (days == null) return null;
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

export const OUTLOOK_LABEL = {
  no_spend: "No spend yet",
  within: "Within cap",
  approaching: "Approaching cap",
  forecast_breach: "Forecast breach",
  breached: "Cap breached",
  no_cap: "No cap set",
};

// Utilization at which a budget reads as "approaching" when its policy sets no soft threshold.
const DEFAULT_SOFT_PCT = 90;

// outlookModel answers "are we likely to stay inside the financial boundary?" — the forward-
// looking question a control screen exists for. A column that only reports whether a breach has
// ALREADY happened is reactive: by the time it lights up, the money is spent. So the state ladder
// runs from spent-nothing to projected-past-cap to actually-over, and each state carries the
// evidence behind it (projected utilization, and the euro overage when there is one).
//
//   state: "no_cap" | "no_spend" | "within" | "approaching" | "forecast_breach" | "breached"
//   tone:  "neutral" for the unremarkable states — colour is spent only where it buys attention.
export function outlookModel(row, marks) {
  const cap = Number(row && row.budget) || 0;
  const actual = Number(row && row.actual) || 0;
  const nowPct = utilizationPct(row);
  const projectedPct =
    row && row.forecast_utilization_pct != null ? Math.round(row.forecast_utilization_pct) : null;
  const make = (state, tone, overage) => ({
    state,
    label: OUTLOOK_LABEL[state],
    tone,
    projectedPct,
    overage: overage != null && overage > 0 ? overage : null,
  });

  if (!cap) return make("no_cap", "neutral", null);
  // Already past the cap: the overage is realized, so quote the actual gap.
  if (nowPct >= 100 || (row && row.status === "breached_actual")) {
    return make("breached", "danger", actual - cap);
  }
  // On course to cross it. Prefer the backend's projected overage; fall back to the forecast %.
  const projOver = projectedPct != null && projectedPct > 100;
  if ((row && row.status === "breached_forecast") || projOver) {
    const overage =
      row && row.projected_overage != null
        ? Number(row.projected_overage)
        : projectedPct != null
        ? (projectedPct / 100) * cap - cap
        : null;
    return make("forecast_breach", "warning", overage);
  }
  if (nowPct === 0 && !projectedPct) return make("no_spend", "neutral", null);
  const soft = marks && marks.soft != null ? marks.soft : DEFAULT_SOFT_PCT;
  const approaching =
    normalizeStatus(row && row.status) === "at_risk" ||
    nowPct >= soft ||
    (projectedPct != null && projectedPct >= soft);
  return make(approaching ? "approaching" : "within", approaching ? "warning" : "neutral", null);
}

// riskRowModel assembles the view model for an "At risk" card: the scope row
// plus its owner/scope labels, now/projected utilization, threshold marks,
// humanized ETA, governing policy, and a plain-language coverage note.
export function riskRowModel(row, policies) {
  const policy = effectivePolicy(row, policies);
  const n = normalizeStatus(row.status);
  const nowPct = utilizationPct(row);
  const projectedPct =
    row.forecast_utilization_pct != null ? Math.round(row.forecast_utilization_pct) : null;
  const governed = policy ? { id: policy.id, name: policy.name } : null;
  const hasGate = policyHasSignoffGate(policy);
  let coverageNote;
  if (governed && n === "breaching" && hasGate) coverageNote = "Hard cap crossed · approval required";
  else if (governed) coverageNote = `Governed by ${policy.name}`;
  else coverageNote = "No policy attached — breaches aren't gated";
  // The cap (100% of the budget limit) is always a real threshold, so the bar shows a
  // cap mark even when ungoverned — that is precisely the case where the breach had no
  // soft/hard policy ladder to catch it. A governing policy adds its own soft/hard marks.
  const marks = policy ? thresholdMarks(policy) : { soft: null, hard: 100 };
  return {
    ...row,
    ownerLabel: row.owner_name || "Unassigned",
    scopeLabel: scopeLevelLabel(row.scope_level),
    nowPct,
    projectedPct,
    marks,
    etaLabel: humanizeBreachEta(row.breach_eta_days),
    governed,
    ungoverned: !governed,
    policyHasGate: hasGate,
    coverageNote,
  };
}
